/**
 * RerouteModal - 路线编辑器 Modal
 *
 * 可视化显示和编辑任务路线：
 * - 显示当前路线的所有节点（支持拖拽排序）
 * - 每个节点显示 Action Badge（Pick/Drop/Liveload/Return/Store）
 * - 每个节点后有 [Change] 和 [+] 按钮
 * - Change: 修改该节点的目的地
 * - +: 在该节点后添加新的停靠点
 * - 底部选择 Drop/Liveload（针对整个任务）
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  type Modifier
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  X,
  MapPin,
  Warehouse,
  Ship,
  Building2,
  UserCircle,
  Briefcase,
  Plus,
  Trash2,
  AlertCircle,
  GripVertical
} from 'lucide-react'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'

// Route node in the route
export interface RouteNode {
  id?: number
  type: 'warehouse' | 'yard' | 'port' | 'shipper' | 'client' | 'custom'
  name: string
  address?: string
  contact?: string
  phone?: string
  _sortId?: string
  action?: string // Backend valid: 'pick' | 'delivery' | 'return' | 'store' | 'inspection' | 'rejected' | 'handover'
}

export interface RerouteData {
  routeNodes: RouteNode[]
  taskBehavior: 'drop' | 'liveload'
  reason?: string
}

interface Destination {
  id: number
  type: 'warehouse' | 'yard' | 'port' | 'shipper' | 'client'
  name: string
  shortName?: string
  address?: string
  contact?: string
  phone?: string
  city?: string
  state?: string
}

interface RerouteModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: RerouteData) => Promise<void>
  isSaving?: boolean
  taskId: string
  containerNumber?: string
  initialRoute: RouteNode[]
  destinations: Destination[]
  /** Groups whose fetch failed. Named here so a dropped group cannot masquerade
   *  as "this company has none of those" — see `destinationsLoading` too. */
  failedDestinationGroups?: string[]
  destinationsLoading?: boolean
  /** Refetch just the named groups. */
  onRetryDestinations?: (groups: string[]) => void
  currentTaskBehavior?: 'drop' | 'liveload'
  /** The port to return container to (for liveload tasks) */
  returnPort?: RouteNode
}

// Icon mapping for destination types
const TYPE_ICONS: Record<string, React.ElementType> = {
  warehouse: Warehouse,
  yard: Building2,
  port: Ship,
  shipper: Briefcase,
  client: UserCircle,
  custom: MapPin
}

const TYPE_COLORS: Record<string, string> = {
  warehouse: 'text-orange-500',
  yard: 'text-purple-500',
  port: 'text-blue-500',
  shipper: 'text-green-500',
  client: 'text-teal-500',
  custom: 'text-gray-500'
}

const TYPE_BG_COLORS: Record<string, string> = {
  warehouse: 'bg-orange-50 border-orange-200',
  yard: 'bg-purple-50 border-purple-200',
  port: 'bg-blue-50 border-blue-200',
  shipper: 'bg-green-50 border-green-200',
  client: 'bg-teal-50 border-teal-200',
  custom: 'bg-gray-50 border-gray-200'
}

// ── Sort ID helper ──────────────────────────────────────────────────
let sortIdCounter = 0
function assignSortId(node: RouteNode): RouteNode {
  if (node._sortId) return node
  return { ...node, _sortId: `node-${++sortIdCounter}-${Date.now()}` }
}
/** Clone a node with a fresh _sortId (used when re-adding an existing node) */
function cloneWithNewSortId(node: RouteNode): RouteNode {
  const { _sortId, ...rest } = node
  return assignSortId(rest)
}

// ── Action Badge system ──────────────────────────────────────────────
interface ActionBadge {
  label: string
  bg: string
  text: string
}

// Action display options — keys must match backend valid actions:
// pick, delivery, return, store, inspection, rejected, handover
const ACTION_OPTIONS: Record<string, ActionBadge> = {
  pick:       { label: 'Pick',       bg: 'bg-blue-100',   text: 'text-blue-700' },
  delivery:   { label: 'Drop',       bg: 'bg-orange-100', text: 'text-orange-700' },
  return:     { label: 'Return',     bg: 'bg-blue-100',   text: 'text-blue-700' },
  store:      { label: 'Store',      bg: 'bg-purple-100', text: 'text-purple-700' },
  inspection: { label: 'Inspection', bg: 'bg-amber-100',  text: 'text-amber-700' },
  rejected:   { label: 'Rejected',   bg: 'bg-red-100',    text: 'text-red-700' },
  handover:   { label: 'Handover',   bg: 'bg-gray-100',   text: 'text-gray-700' },
}
const ALL_ACTION_KEYS = Object.keys(ACTION_OPTIONS)

/** Compute the default action key for a node based on position + type + behavior.
 *  Returns backend-valid action values only. */
function getDefaultAction(
  index: number,
  node: RouteNode,
  _taskBehavior: 'drop' | 'liveload'
): string {
  if (index === 0) return 'pick'
  switch (node.type) {
    case 'port':      return 'return'
    case 'yard':      return 'store'
    case 'warehouse': return 'delivery'
    default:          return 'delivery'
  }
}

/** Look up badge styling from a node's action (with fallback) */
function getActionBadge(node: RouteNode, index: number, taskBehavior: 'drop' | 'liveload'): ActionBadge {
  const key = node.action || getDefaultAction(index, node, taskBehavior)
  return ACTION_OPTIONS[key] || ACTION_OPTIONS.delivery
}

/** Assign default action to a node for a given position + behavior */
function assignDefaultAction(
  node: RouteNode,
  index: number,
  taskBehavior: 'drop' | 'liveload'
): RouteNode {
  return { ...node, action: getDefaultAction(index, node, taskBehavior) }
}

// ── Restrict drag to vertical axis ─────────────────────────────────
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0
})

// ── SortableRouteNode component ─────────────────────────────────────
interface SortableRouteNodeProps {
  node: RouteNode
  index: number
  totalNodes: number
  taskBehavior: 'drop' | 'liveload'
  isEditing: boolean
  isAddingAfter: boolean
  isDropdownOpen: boolean
  isEditingAction: boolean
  canDelete: boolean
  onChangeClick: (index: number) => void
  onAddClick: (index: number) => void
  onDeleteNode: (index: number) => void
  onBadgeClick: (index: number) => void
  onActionSelect: (index: number, action: string) => void
  renderDropdown: () => React.ReactNode
}

const SortableRouteNode: React.FC<SortableRouteNodeProps> = ({
  node,
  index,
  totalNodes,
  taskBehavior,
  isEditing,
  isAddingAfter,
  isDropdownOpen,
  isEditingAction,
  canDelete,
  onChangeClick,
  onAddClick,
  onDeleteNode,
  onBadgeClick,
  onActionSelect,
  renderDropdown
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: node._sortId! })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto'
  }

  const Icon = TYPE_ICONS[node.type] || MapPin
  const iconColor = TYPE_COLORS[node.type] || 'text-gray-500'
  const bgColor = TYPE_BG_COLORS[node.type] || 'bg-gray-50 border-gray-200'
  const badge = getActionBadge(node, index, taskBehavior)
  const currentActionKey = node.action || getDefaultAction(index, node, taskBehavior)

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative">
      {/* Node Card */}
      <div
        className={`flex items-center gap-2 px-2 py-2 rounded-lg border ${bgColor} ${
          isEditing ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        {/* Drag Handle */}
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          className="p-0.5 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
          tabIndex={-1}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Icon */}
        <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{node.name}</p>
          {node.address && (
            <p className="text-xs text-gray-500 truncate">{node.address}</p>
          )}
        </div>

        {/* Clickable Action Badge */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => onBadgeClick(index)}
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text} hover:ring-2 hover:ring-gray-300 transition-all cursor-pointer`}
            title="Click to change action"
          >
            {badge.label}
          </button>

          {/* Action picker popover */}
          {isEditingAction && (
            <div className="absolute right-0 top-full mt-1 z-30 bg-white rounded-lg shadow-lg border border-gray-200 p-1.5 flex flex-wrap gap-1 w-48">
              {ALL_ACTION_KEYS.map(key => {
                const opt = ACTION_OPTIONS[key]
                const isActive = key === currentActionKey
                return (
                  <button
                    key={key}
                    onClick={() => onActionSelect(index, key)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all ${opt.bg} ${opt.text} ${
                      isActive ? 'ring-2 ring-offset-1 ring-gray-400' : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onChangeClick(index)}
            className="px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-100 rounded transition-colors"
          >
            Change
          </button>
          <button
            onClick={() => onAddClick(index)}
            className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-100 rounded transition-colors"
            title="Add stop after this"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {canDelete && (
            <button
              onClick={() => onDeleteNode(index)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
              title="Remove this stop"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown for editing this node */}
      {isEditing && isDropdownOpen && (
        <div className="absolute left-0 right-0 z-20">
          {renderDropdown()}
        </div>
      )}

      {/* Connection Line + Add indicator */}
      {index < totalNodes - 1 && (
        <div className="relative flex items-center justify-center py-1">
          <div className="w-0.5 h-4 bg-gray-300"></div>
          {/* Dropdown for adding after this node */}
          {isAddingAfter && isDropdownOpen && (
            <div className="absolute left-0 top-full z-20">
              {renderDropdown()}
            </div>
          )}
        </div>
      )}

      {/* Dropdown for adding after last node */}
      {index === totalNodes - 1 && isAddingAfter && isDropdownOpen && (
        <div className="absolute left-0 right-0 mt-2 z-20">
          {renderDropdown()}
        </div>
      )}
    </div>
  )
}

// ── Static overlay card (shown while dragging) ─────────────────────
// Badge comes from node.action (persisted), so it's safe to show.
interface OverlayNodeProps {
  node: RouteNode
}

const OverlayNode: React.FC<OverlayNodeProps> = ({ node }) => {
  const Icon = TYPE_ICONS[node.type] || MapPin
  const iconColor = TYPE_COLORS[node.type] || 'text-gray-500'
  const bgColor = TYPE_BG_COLORS[node.type] || 'bg-gray-50 border-gray-200'
  const badge = node.action ? ACTION_OPTIONS[node.action] : null

  return (
    <div
      className={`flex items-center gap-2 px-2 py-2 rounded-lg border ${bgColor} shadow-lg ring-2 ring-blue-400`}
    >
      <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{node.name}</p>
        {node.address && (
          <p className="text-xs text-gray-500 truncate">{node.address}</p>
        )}
      </div>
      {badge && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.bg} ${badge.text} flex-shrink-0`}>
          {badge.label}
        </span>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════
export const RerouteModal: React.FC<RerouteModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving = false,
  taskId,
  containerNumber,
  initialRoute,
  destinations,
  failedDestinationGroups = [],
  destinationsLoading = false,
  onRetryDestinations,
  currentTaskBehavior = 'drop',
  returnPort
}) => {
  // Route nodes state
  const [routeNodes, setRouteNodes] = useState<RouteNode[]>([])
  const [taskBehavior, setTaskBehavior] = useState<'drop' | 'liveload'>('drop')
  const [reason, setReason] = useState('')

  // Editing state
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [addingAfterIndex, setAddingAfterIndex] = useState<number | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Custom address state
  const [showCustomAddressForm, setShowCustomAddressForm] = useState(false)
  const [customAddressName, setCustomAddressName] = useState('')
  const [customAddressValue, setCustomAddressValue] = useState('')

  // Store initial port from route (for liveload return)
  const [initialPort, setInitialPort] = useState<RouteNode | null>(null)

  // Action badge editing state
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null)

  // Drag state
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const dropdownRef = useRef<HTMLDivElement>(null)

  // Track if modal was previously open to detect open transition
  const prevOpenRef = useRef(false)

  // Reset form ONLY when modal transitions from closed to open
  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open

    // Only initialize when modal just opened (was closed, now open)
    if (open && !wasOpen) {
      console.log('[RerouteModal] Modal opened, initializing with:', { initialRoute, currentTaskBehavior })
      setRouteNodes(
        initialRoute.length > 0
          ? initialRoute.map((n, i) => assignDefaultAction(assignSortId(n), i, currentTaskBehavior))
          : []
      )
      setTaskBehavior(currentTaskBehavior)
      setReason('')
      setEditingIndex(null)
      setAddingAfterIndex(null)
      setEditingActionIndex(null)
      setSearchValue('')
      setIsDropdownOpen(false)
      setShowCustomAddressForm(false)
      setCustomAddressName('')
      setCustomAddressValue('')
      setActiveDragId(null)

      // Store the initial port (first node of type 'port') for potential liveload return
      const portNode = returnPort || initialRoute.find(node => node.type === 'port')
      setInitialPort(portNode ? assignSortId(portNode) : null)
    }
  }, [open, initialRoute, currentTaskBehavior, returnPort])

  // Handle task behavior change - automatically adjust route for drop/liveload
  const handleTaskBehaviorChange = (newBehavior: 'drop' | 'liveload') => {
    if (newBehavior === taskBehavior) return

    const oldBehavior = taskBehavior
    setTaskBehavior(newBehavior)
    setEditingActionIndex(null)

    // Recalculate warehouse node actions — all warehouse nodes use 'delivery' (backend-valid)
    // The drop/liveload distinction is conveyed via taskBehavior, not per-node action
    const updatedNodes = routeNodes.map((node, i) => {
      if (node.type === 'warehouse' && i > 0) {
        // Normalize any legacy 'drop'/'liveload' action values to 'delivery'
        if (!node.action || node.action === 'drop' || node.action === 'liveload') {
          return { ...node, action: 'delivery' }
        }
      }
      return node
    })

    // Get the last node in current route
    const lastNode = updatedNodes[updatedNodes.length - 1]
    const isLastNodePort = lastNode?.type === 'port'

    if (newBehavior === 'liveload') {
      // Switching to liveload: need to return container to port
      if (!isLastNodePort && initialPort) {
        const portNode = assignDefaultAction(
          cloneWithNewSortId(initialPort),
          updatedNodes.length,
          newBehavior
        )
        setRouteNodes([...updatedNodes, portNode])
      } else if (!isLastNodePort && !initialPort) {
        const portDest = destinations.find(d => d.type === 'port')
        if (portDest) {
          const portNode = assignDefaultAction(
            assignSortId({ id: portDest.id, type: 'port', name: portDest.name, address: portDest.address }),
            updatedNodes.length,
            newBehavior
          )
          setRouteNodes([...updatedNodes, portNode])
        } else {
          setRouteNodes(updatedNodes)
        }
      } else {
        setRouteNodes(updatedNodes)
      }
    } else {
      // Switching to drop: remove port from end if it's there
      if (isLastNodePort && updatedNodes.length > 1) {
        setRouteNodes(updatedNodes.slice(0, -1))
      } else {
        setRouteNodes(updatedNodes)
      }
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setEditingIndex(null)
        setAddingAfterIndex(null)
        setEditingActionIndex(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Group destinations by type.
  // Only the three places a container can actually be rerouted to: a warehouse,
  // a client, or a terminal. Yards are deliberately absent — parking a box in
  // the yard is Store to Yard, which carries its own storage billing and its own
  // cancel path; reaching it through Reroute produces a task that looks stored
  // but was never booked as such. Shippers are a billing role, not a stop.
  const groupedDestinations = useMemo(() => {
    // The row shows "Everport Terminal Services (ETS)", so the short name has to
    // be searchable too — matching only the full name meant operators had to type
    // the long spelling of a place they know by its abbreviation.
    const search = searchValue.trim().toLowerCase()
    const filtered = destinations.filter(d => {
      if (!search) return true
      return (
        d.name.toLowerCase().includes(search) ||
        (d.shortName?.toLowerCase().includes(search)) ||
        (d.address?.toLowerCase().includes(search)) ||
        (d.city?.toLowerCase().includes(search))
      )
    })

    return {
      warehouses: filtered.filter(d => d.type === 'warehouse'),
      ports: filtered.filter(d => d.type === 'port'),
      clients: filtered.filter(d => d.type === 'client')
    }
  }, [destinations, searchValue])

  const handleSelectDestination = (dest: Destination) => {
    const baseNode: RouteNode = assignSortId({
      id: dest.id,
      type: dest.type,
      name: dest.name,
      address: dest.address,
      contact: dest.contact,
      phone: dest.phone
    })

    if (editingIndex !== null) {
      const newNode = assignDefaultAction(baseNode, editingIndex, taskBehavior)
      const newNodes = [...routeNodes]
      newNodes[editingIndex] = newNode
      setRouteNodes(newNodes)
      setEditingIndex(null)
    } else if (addingAfterIndex !== null) {
      const targetIndex = addingAfterIndex + 1
      const newNode = assignDefaultAction(baseNode, targetIndex, taskBehavior)
      const newNodes = [...routeNodes]
      newNodes.splice(targetIndex, 0, newNode)
      setRouteNodes(newNodes)
      setAddingAfterIndex(null)
    }

    setSearchValue('')
    setIsDropdownOpen(false)
  }

  // Handle custom address selection
  const handleSelectCustomAddress = () => {
    setIsDropdownOpen(false)
    setShowCustomAddressForm(true)
    setCustomAddressName('')
    setCustomAddressValue('')
  }

  // Handle custom address form submit
  const handleCustomAddressSubmit = () => {
    if (!customAddressValue.trim()) return

    const baseNode: RouteNode = assignSortId({
      type: 'custom',
      name: customAddressName.trim() || 'Custom Location',
      address: customAddressValue.trim()
    })

    if (editingIndex !== null) {
      const newNode = assignDefaultAction(baseNode, editingIndex, taskBehavior)
      const newNodes = [...routeNodes]
      newNodes[editingIndex] = newNode
      setRouteNodes(newNodes)
      setEditingIndex(null)
    } else if (addingAfterIndex !== null) {
      const targetIndex = addingAfterIndex + 1
      const newNode = assignDefaultAction(baseNode, targetIndex, taskBehavior)
      const newNodes = [...routeNodes]
      newNodes.splice(targetIndex, 0, newNode)
      setRouteNodes(newNodes)
      setAddingAfterIndex(null)
    }

    setShowCustomAddressForm(false)
    setCustomAddressName('')
    setCustomAddressValue('')
  }

  // Cancel custom address form
  const handleCustomAddressCancel = () => {
    setShowCustomAddressForm(false)
    setCustomAddressName('')
    setCustomAddressValue('')
    setEditingIndex(null)
    setAddingAfterIndex(null)
  }

  // ── Action badge handlers ─────────────────────────────────────────
  const handleBadgeClick = (index: number) => {
    // Toggle: click again to close
    setEditingActionIndex(prev => prev === index ? null : index)
    // Close other popups
    setEditingIndex(null)
    setAddingAfterIndex(null)
    setIsDropdownOpen(false)
  }

  const handleActionSelect = (index: number, action: string) => {
    const newNodes = [...routeNodes]
    newNodes[index] = { ...newNodes[index], action }
    setRouteNodes(newNodes)
    setEditingActionIndex(null)
  }

  const handleChangeClick = (index: number) => {
    setEditingIndex(index)
    setAddingAfterIndex(null)
    setEditingActionIndex(null)
    setSearchValue('')
    setIsDropdownOpen(true)
  }

  const handleAddClick = (index: number) => {
    setAddingAfterIndex(index)
    setEditingIndex(null)
    setEditingActionIndex(null)
    setSearchValue('')
    setIsDropdownOpen(true)
  }

  // Delete a node from the route
  const handleDeleteNode = (index: number) => {
    if (routeNodes.length <= 1) return

    const newNodes = [...routeNodes]
    newNodes.splice(index, 1)
    setRouteNodes(newNodes)

    setEditingIndex(null)
    setAddingAfterIndex(null)
    setIsDropdownOpen(false)
  }

  // Check if a node can be deleted
  const canDeleteNode = (_index: number): boolean => {
    if (routeNodes.length <= 1) return false
    return true
  }

  // ── Drag handlers ─────────────────────────────────────────────────
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string)
    // Close any open dropdowns/popovers
    setEditingIndex(null)
    setAddingAfterIndex(null)
    setIsDropdownOpen(false)
    setEditingActionIndex(null)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveDragId(null)
    // Defensively reset editing state
    setEditingIndex(null)
    setAddingAfterIndex(null)
    setIsDropdownOpen(false)
    setEditingActionIndex(null)

    if (over && active.id !== over.id) {
      setRouteNodes(prev => {
        const oldIndex = prev.findIndex(n => n._sortId === active.id)
        const newIndex = prev.findIndex(n => n._sortId === over.id)
        if (oldIndex === -1 || newIndex === -1) return prev
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }, [])

  // Derive active node for overlay
  const activeDragNode = activeDragId
    ? routeNodes.find(n => n._sortId === activeDragId)
    : null

  const sortIds = useMemo(
    () => routeNodes.map(n => n._sortId!),
    [routeNodes]
  )

  const handleConfirm = async () => {
    // Strip _sortId before sending to API (keep action)
    const cleanNodes = routeNodes.map(({ _sortId, ...rest }) => rest)
    await onConfirm({
      routeNodes: cleanNodes,
      taskBehavior,
      reason: reason.trim() || undefined
    })
  }

  const isValid = routeNodes.length > 0

  if (!open) return null

  const groupFailed = (group: string) => failedDestinationGroups.includes(group)
  const anyGroupFailed = ['warehouse', 'port', 'client'].some(groupFailed)
  const allGroupsEmpty =
    groupedDestinations.warehouses.length === 0 &&
    groupedDestinations.ports.length === 0 &&
    groupedDestinations.clients.length === 0

  /** A group that failed to load says so, in the slot where it would have been.
   *  It must never fall through to "no results" — an incomplete list that reads
   *  as an empty one is how a missing terminal went unnoticed for a whole day. */
  const renderGroupFailure = (group: string, label: string, headerClass: string) => (
    <div>
      <div className={headerClass}>{label}</div>
      <div className="px-3 py-2 flex items-center gap-2.5 bg-red-50/60">
        <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertCircle className="w-3.5 h-3.5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-red-700">Couldn&apos;t load {label.toLowerCase()}</p>
          <p className="text-xs text-red-500">This list is incomplete, not empty.</p>
        </div>
        <button
          onClick={() => onRetryDestinations?.([group])}
          disabled={destinationsLoading}
          className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded transition-colors disabled:opacity-50 flex-shrink-0"
        >
          {destinationsLoading ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    </div>
  )

  const WAREHOUSE_HEADER = 'px-3 py-1.5 bg-gradient-to-r from-orange-50 to-transparent text-[10px] font-bold text-orange-600 uppercase tracking-wider sticky top-0'
  const PORT_HEADER = 'px-3 py-1.5 bg-gradient-to-r from-blue-50 to-transparent text-[10px] font-bold text-blue-600 uppercase tracking-wider sticky top-0'
  const CLIENT_HEADER = 'px-3 py-1.5 bg-gradient-to-r from-teal-50 to-transparent text-[10px] font-bold text-teal-600 uppercase tracking-wider sticky top-0'

  // Render destination dropdown
  const renderDropdown = () => (
    <div className="absolute z-20 w-80 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Search input - sticky header */}
      <div className="sticky top-0 p-3 bg-white/95 backdrop-blur-sm">
        <div className="relative">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search destinations..."
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            autoFocus
          />
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Scrollable content */}
      <div className="max-h-56 overflow-y-auto">
        {/* Warehouses */}
        {groupFailed('warehouse') && renderGroupFailure('warehouse', 'Warehouses', WAREHOUSE_HEADER)}
        {groupedDestinations.warehouses.length > 0 && (
          <div>
            <div className={WAREHOUSE_HEADER}>
              Warehouses
            </div>
            {groupedDestinations.warehouses.map((dest) => (
              <button
                key={`warehouse-${dest.id}`}
                onClick={() => handleSelectDestination(dest)}
                className="w-full px-3 py-2 text-left hover:bg-orange-50 flex items-center gap-2.5 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Warehouse className="w-3.5 h-3.5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                  {dest.address && (
                    <p className="text-xs text-gray-500 truncate">{dest.address}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Ports */}
        {groupFailed('port') && renderGroupFailure('port', 'Ports', PORT_HEADER)}
        {groupedDestinations.ports.length > 0 && (
          <div>
            <div className={PORT_HEADER}>
              Ports
            </div>
            {groupedDestinations.ports.map((dest) => (
              <button
                key={`port-${dest.id}`}
                onClick={() => handleSelectDestination(dest)}
                className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2.5 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Ship className="w-3.5 h-3.5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                  {dest.address && (
                    <p className="text-xs text-gray-500 truncate">{dest.address}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Clients */}
        {groupFailed('client') && renderGroupFailure('client', 'Clients', CLIENT_HEADER)}
        {groupedDestinations.clients.length > 0 && (
          <div>
            <div className={CLIENT_HEADER}>
              Clients
            </div>
            {groupedDestinations.clients.map((dest) => (
              <button
                key={`client-${dest.id}`}
                onClick={() => handleSelectDestination(dest)}
                className="w-full px-3 py-2 text-left hover:bg-teal-50 flex items-center gap-2.5 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <UserCircle className="w-3.5 h-3.5 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{dest.name}{dest.shortName && dest.shortName !== dest.name ? ` (${dest.shortName})` : ''}</p>
                  {dest.address && (
                    <p className="text-xs text-gray-500 truncate">{dest.address}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Still fetching and nothing to show yet — say so rather than look empty */}
        {allGroupsEmpty && destinationsLoading && (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Loading destinations…</div>
        )}

        {/* Genuinely nothing on file — distinct from "the fetch failed" above */}
        {allGroupsEmpty && !destinationsLoading && !anyGroupFailed && searchValue.trim() === '' && (
          <div className="px-4 py-6 text-center">
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No warehouses, ports or clients on file</p>
            <p className="text-xs text-gray-400 mt-0.5">Use Custom Address below</p>
          </div>
        )}

        {/* No results message */}
        {allGroupsEmpty &&
         !destinationsLoading &&
         !anyGroupFailed &&
         searchValue.trim() !== '' && (
          <div className="px-4 py-6 text-center">
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No results for "{searchValue}"</p>
          </div>
        )}
      </div>

      {/* Custom Address Option - sticky footer */}
      <div className="sticky bottom-0 border-t border-gray-100 bg-gray-50/95 backdrop-blur-sm">
        <button
          onClick={handleSelectCustomAddress}
          className="w-full px-3 py-2.5 text-left hover:bg-blue-50 flex items-center gap-2.5 transition-colors"
        >
          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Plus className="w-3.5 h-3.5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-blue-600">Custom Address</p>
            <p className="text-xs text-gray-500">Enter location not in system</p>
          </div>
        </button>
      </div>
    </div>
  )

  // z-[100]：必须高过详情弹窗的 z-[70]，否则被它的黑底盖住＝点了没反应
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Edit Route</h3>
              <p className="text-sm text-gray-500">
                {containerNumber ? `Container: ${containerNumber}` : `Task: ${taskId}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
          {/* Route Section */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500 uppercase tracking-wide">Route</Label>

            {/* Route Nodes with DnD */}
            <div className="space-y-0" ref={dropdownRef}>
              {routeNodes.length > 0 ? (
                <DndContext
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                    {routeNodes.map((node, index) => (
                      <SortableRouteNode
                        key={node._sortId}
                        node={node}
                        index={index}
                        totalNodes={routeNodes.length}
                        taskBehavior={taskBehavior}
                        isEditing={editingIndex === index}
                        isAddingAfter={addingAfterIndex === index}
                        isDropdownOpen={isDropdownOpen}
                        isEditingAction={editingActionIndex === index}
                        canDelete={canDeleteNode(index)}
                        onChangeClick={handleChangeClick}
                        onAddClick={handleAddClick}
                        onDeleteNode={handleDeleteNode}
                        onBadgeClick={handleBadgeClick}
                        onActionSelect={handleActionSelect}
                        renderDropdown={renderDropdown}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay dropAnimation={null}>
                    {activeDragNode ? (
                      <OverlayNode node={activeDragNode} />
                    ) : null}
                  </DragOverlay>
                </DndContext>
              ) : (
                /* Empty state */
                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">No route defined</p>
                  <button
                    onClick={() => {
                      setAddingAfterIndex(-1)
                      setIsDropdownOpen(true)
                    }}
                    className="mt-2 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded font-medium"
                  >
                    + Add first stop
                  </button>
                  {addingAfterIndex === -1 && isDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-2 z-20 px-6">
                      {renderDropdown()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Custom Address Form */}
          {showCustomAddressForm && (
            <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Custom Address</span>
                </div>
                <button
                  onClick={handleCustomAddressCancel}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Form Body */}
              <div className="p-4 space-y-4 bg-white">
                {/* Location Name - Inline */}
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium text-gray-500 w-20 flex-shrink-0">
                    Name
                  </label>
                  <input
                    type="text"
                    value={customAddressName}
                    onChange={(e) => setCustomAddressName(e.target.value)}
                    placeholder="Location name (optional)"
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white transition-colors"
                  />
                </div>

                {/* Address - Full width */}
                <div className="flex items-start gap-3">
                  <label className="text-xs font-medium text-gray-500 w-20 flex-shrink-0 pt-2">
                    Address <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={customAddressValue}
                    onChange={(e) => setCustomAddressValue(e.target.value)}
                    placeholder="Street, City, State ZIP"
                    rows={2}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white resize-none transition-colors"
                    autoFocus
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  onClick={handleCustomAddressCancel}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomAddressSubmit}
                  disabled={!customAddressValue.trim()}
                  className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
                    customAddressValue.trim()
                      ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Add Location
                </button>
              </div>
            </div>
          )}

          {/* Task Behavior - Drop / Liveload */}
          <div className="space-y-2 pt-4 border-t border-gray-100">
            <Label className="text-xs text-gray-500 uppercase tracking-wide">Task Type</Label>
            <div className="flex gap-2">
              <button
                onClick={() => handleTaskBehaviorChange('drop')}
                className={`flex-1 py-2 rounded-lg border-2 text-center transition-all text-sm ${
                  taskBehavior === 'drop'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <span className="font-medium">Drop</span>
              </button>
              <button
                onClick={() => handleTaskBehaviorChange('liveload')}
                className={`flex-1 py-2 rounded-lg border-2 text-center transition-all text-sm ${
                  taskBehavior === 'liveload'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                <span className="font-medium">Liveload</span>
              </button>
            </div>

            {/* Info message about route changes */}
            {taskBehavior === 'liveload' && (() => {
              const lastNode = routeNodes[routeNodes.length - 1]
              const hasReturnPort = lastNode?.type === 'port'
              return hasReturnPort ? (
                <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Liveload will return container to <strong>{lastNode.name}</strong> after loading.</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium">Return port not set.</span>
                    <span className="ml-1">Add a port as the final destination, or the return location will be marked as pending.</span>
                  </div>
                </div>
              )
            })()}
            {taskBehavior === 'drop' && (
              <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Drop means the container stays at the destination. No return to port needed.</span>
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500 uppercase tracking-wide">Reason</Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for route modification..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSaving || !isValid}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {isSaving ? 'Saving...' : 'Confirm'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
